/**
 * A channel's (or category's) permissions, as Discord's channel settings
 * have them (docs/design/roles-permissions.md, O1-O8):
 *
 * - overwrites for roles AND members, picked from the left; the right side
 *   lists every permission that channel type takes, in Discord's groups,
 *   each Allow / Inherit (follow the role) / Deny;
 * - "Synced with the category" or "Not synced" with Sync now; a change on a
 *   synced channel says it breaks the sync; a category says its synced
 *   channels follow it (the server carries the change to them);
 * - the hierarchy and the grant rule shown the way the server applies them:
 *   targets at or above your highest role are locked, and bits you don't
 *   hold here are off unless you are the owner, an Administrator, or hold
 *   Manage Permissions through this channel's own overwrites;
 * - a warning whenever Manage Permissions is allowed here; only the owner
 *   and Administrators may allow it (the server's rule), so nobody hands it
 *   to themselves;
 * - a deleted role's (or a departed member's) overwrite is read-only, with
 *   only Remove (the server carries it as it is or removes it).
 *
 * The editor works on a draft; ChannelEditor saves it (PUT/DELETE per
 * changed target), except "Sync now", which applies at once.
 */
import {
  OverwriteType,
  overwritesInSync,
  parsePermissions,
  Permissions,
  serializePermissions,
  type Channel,
  type Overwrite,
  type Snowflake,
} from '@neosocial/protocol';
import { useMemo, useState } from 'react';
import { useT } from '../i18n';
import { useData, useDataShallow } from '../store/data';
import {
  basePermissions,
  can,
  channelPermissions,
  displayName,
  roleRankIn,
  sortedRoles,
  topRolePosition,
  viewAsRoles,
} from '../store/selectors';
import { Avatar, Btn, Icon, Select } from '../ui/kit';
import { overwriteGroups, type ChannelKind, type PermDef } from './permDefs';

/** The three-way switch: Deny / Inherit / Allow. */
export function TriState({
  value,
  onChange,
  label,
  disabled,
  why,
  allowOff,
}: {
  value: -1 | 0 | 1;
  onChange(v: -1 | 0 | 1): void;
  label: string;
  disabled?: boolean;
  /** Tooltip when disabled. */
  why?: string;
  /** Only Allow is off (Deny and Inherit still work), with this reason. */
  allowOff?: string;
}) {
  const { t } = useT();
  return (
    <div
      className="ns-tri"
      role="radiogroup"
      aria-label={label}
      {...(disabled && why ? { 'data-tip': why } : {})}
    >
      {([-1, 0, 1] as const).map((v) => {
        const off = disabled || (v === 1 && value !== 1 && !!allowOff);
        const tip =
          v === 1 && allowOff && !disabled && value !== 1
            ? allowOff
            : t(v === -1 ? 'ns.ow.deny' : v === 1 ? 'ns.ow.allow' : 'ns.ow.inherit');
        return (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={value === v}
            disabled={off}
            data-v={v}
            aria-label={t(v === -1 ? 'ns.ow.deny' : v === 1 ? 'ns.ow.allow' : 'ns.ow.inherit')}
            {...(!disabled ? { 'data-tip': tip } : {})}
            onClick={() => onChange(v)}
          >
            <Icon name={v === -1 ? 'x' : v === 1 ? 'check' : 'slash'} />
          </button>
        );
      })}
    </div>
  );
}

export function owValue(ow: Overwrite | undefined, def: PermDef): -1 | 0 | 1 {
  if (!ow) return 0;
  const allow = parsePermissions(def.nx ? ow.nx_allow : ow.allow);
  const deny = parsePermissions(def.nx ? ow.nx_deny : ow.deny);
  if ((allow & def.bit) !== 0n) return 1;
  if ((deny & def.bit) !== 0n) return -1;
  return 0;
}

export function setOw(ow: Overwrite, def: PermDef, v: -1 | 0 | 1): Overwrite {
  const ak = def.nx ? 'nx_allow' : 'allow';
  const dk = def.nx ? 'nx_deny' : 'deny';
  let allow = parsePermissions(ow[ak]) & ~def.bit;
  let deny = parsePermissions(ow[dk]) & ~def.bit;
  if (v === 1) allow |= def.bit;
  if (v === -1) deny |= def.bit;
  return { ...ow, [ak]: serializePermissions(allow), [dk]: serializePermissions(deny) };
}

const EMPTY_OW = (id: Snowflake, type: 0 | 1): Overwrite => ({
  id,
  type,
  allow: '0',
  deny: '0',
  nx_allow: '0',
  nx_deny: '0',
});

export function ChannelPermissions({
  guildId,
  channel,
  kind,
  parentId,
  value,
  onChange,
  onSync,
  syncing,
  canSync = true,
}: {
  guildId: Snowflake;
  /** The saved channel (undefined while creating one). */
  channel: Channel | undefined;
  kind: ChannelKind;
  /** The draft's category. */
  parentId: Snowflake | null;
  value: Overwrite[];
  onChange(next: Overwrite[]): void;
  /** Copies the category's permissions (at once for a saved channel). */
  onSync(categoryOverwrites: Overwrite[]): void;
  syncing?: boolean;
  /** Sync now on a saved channel is a channel edit: it takes Manage channels there. */
  canSync?: boolean;
}) {
  const { t } = useT();
  const guild = useData((d) => d.guilds[guildId]);
  const members = useData((d) => d.members[guildId]);
  const parent = useData((d) => (parentId ? d.channels[parentId] : undefined));
  const me = useData((d) => d.me?.id ?? '');
  const myRank = useData((d) => (me ? topRolePosition(d, guildId, me) : 0));
  // Your permissions where the overwrite applies, as the server computes
  // them: in the channel; while creating one, in its category (server-wide
  // without one).
  const base = useDataShallow((d) => basePermissions(d, guildId));
  const mine = useDataShallow((d) =>
    channel ? channelPermissions(d, channel) : parent ? channelPermissions(d, parent) : null,
  );
  const held = mine ?? base;
  // while viewing the server as a role you are not the owner
  const previewing = useData((d) => !!viewAsRoles(d, guildId));
  const isOwner = !!guild && guild.owner_id === me && !previewing;
  const privileged = isOwner || can(base, Permissions.ADMINISTRATOR);
  const canEdit = isOwner || can(held, Permissions.MANAGE_ROLES);
  const myRoles = useData((d) => d.members[guildId]?.[me]?.roles);
  // Discord's exception: Manage Permissions through this channel's own overwrites.
  const viaOverwrite =
    !!channel &&
    can(held, Permissions.MANAGE_ROLES) &&
    (channel.permission_overwrites ?? []).some(
      (o) =>
        (parsePermissions(o.allow) & Permissions.MANAGE_ROLES) !== 0n &&
        (o.type === OverwriteType.MEMBER
          ? o.id === me
          : o.id === guildId || (myRoles ?? []).includes(o.id)),
    );
  const [sel, setSel] = useState<Snowflake | null>(value[0]?.id ?? null);
  const selected = value.find((o) => o.id === sel) ?? value[0];
  const groups = useMemo(() => overwriteGroups(kind), [kind]);

  const roleOf = (id: Snowflake) => guild?.roles.find((r) => r.id === id);
  const nameOf = (o: Overwrite) =>
    o.type === OverwriteType.ROLE
      ? o.id === guildId
        ? '@everyone'
        : (roleOf(o.id)?.name ?? t('ow.deletedRole'))
      : members?.[o.id]
        ? displayName(members[o.id]!.user, members[o.id])
        : t('ow.leftMember');
  /** A deleted role's (or a departed member's) overwrite: kept or removed, never edited (the server's rule). */
  const gone = (o: Overwrite) =>
    o.type === OverwriteType.ROLE
      ? o.id !== guildId && !!guild && !roleOf(o.id)
      : !!members && !members[o.id];
  /** The hierarchy, as the server checks it. */
  const locked = (o: Overwrite) => {
    if (isOwner) return false;
    if (o.type === OverwriteType.ROLE) {
      if (o.id === guildId) return false;
      const r = roleOf(o.id);
      return !!r && !!guild && roleRankIn(guild, r) >= myRank;
    }
    if (o.id === me) return false;
    if (guild && o.id === guild.owner_id) return true;
    const m = members?.[o.id];
    if (!m || !guild) return false;
    return topRolePosition(useData.getState(), guildId, o.id) >= myRank;
  };
  const holds = (d: PermDef) =>
    privileged ||
    (d.nx
      ? (held.nx_permissions & d.bit) !== 0n
      : viaOverwrite || (held.permissions & d.bit) !== 0n);

  const synced =
    kind !== 'category' && !!parent && overwritesInSync(value, parent.permission_overwrites);
  const wasSynced =
    kind !== 'category' &&
    !!parent &&
    !!channel &&
    channel.parent_id === parent.id &&
    overwritesInSync(channel.permission_overwrites, parent.permission_overwrites);

  const roleOptions = sortedRoles(guild).filter(
    (r) =>
      !value.some((o) => o.id === r.id) &&
      (isOwner || r.id === guildId || (!!guild && roleRankIn(guild, r) < myRank)),
  );
  const memberOptions = Object.values(members ?? {})
    .filter((m) => !value.some((o) => o.id === m.user.id))
    .filter((m) => !locked(EMPTY_OW(m.user.id, 1)))
    .sort((a, b) => displayName(a.user, a).localeCompare(displayName(b.user, b)));

  const add = (id: Snowflake, type: 0 | 1) => {
    if (!id) return;
    onChange([...value, EMPTY_OW(id, type)]);
    setSel(id);
  };
  const managePermsAllowed = value.some(
    (o) => (parsePermissions(o.allow) & Permissions.MANAGE_ROLES) !== 0n,
  );

  return (
    <div className="ns-adm-ow ns-chp">
      <h4>{t('ow.title')}</h4>
      {!canEdit ? <p className="ns-muted">{t('ns.admin.owNoPerm')}</p> : null}
      {kind === 'category' ? (
        <p className="ns-note">
          <Icon name="layers" /> {t('ow.categoryNote')}
        </p>
      ) : parent ? (
        <div className={'ns-note' + (synced ? ' ok' : '')} data-synced={synced ? 'true' : 'false'}>
          <Icon name={synced ? 'check-circle' : 'info'} />
          <span dir="auto">
            {t(synced ? 'ow.synced' : 'ow.notSynced', { category: parent.name ?? '' })}
          </span>
          {!synced && canEdit && canSync ? (
            <Btn
              size="xs"
              kind="mode"
              busy={syncing}
              disabled={syncing}
              onClick={() => onSync(parent.permission_overwrites ?? [])}
            >
              {t('ow.sync')}
            </Btn>
          ) : null}
        </div>
      ) : null}
      {wasSynced && !synced ? (
        <p className="ns-note warn">
          <Icon name="alert" /> {t('ow.breaksSync', { category: parent?.name ?? '' })}
        </p>
      ) : null}
      {managePermsAllowed ? (
        <p className="ns-note warn" data-warn="manage-permissions">
          <Icon name="alert" /> {t('ow.managePermsWarn')}
        </p>
      ) : null}
      <div className="ns-chp-split">
        <div className="ns-chp-targets">
          <ul role="listbox" aria-label={t('ow.title')}>
            {value.map((o) => (
              <li key={o.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected?.id === o.id}
                  className="ns-chp-t"
                  onClick={() => setSel(o.id)}
                >
                  {o.type === OverwriteType.ROLE ? (
                    <i
                      className="ns-role-dot"
                      style={{
                        background: roleOf(o.id)?.color
                          ? '#' + roleOf(o.id)!.color.toString(16).padStart(6, '0')
                          : 'var(--text-3)',
                      }}
                    />
                  ) : (
                    <Avatar user={members?.[o.id]?.user} size={18} />
                  )}
                  <span dir="auto">{nameOf(o)}</span>
                  {locked(o) ? <Icon name="lock" cls="ns-chp-lock" /> : null}
                </button>
              </li>
            ))}
          </ul>
          {!value.length ? <p className="ns-muted sm">{t('ow.none')}</p> : null}
          {canEdit ? (
            <div className="ns-chp-add">
              <Select
                className="nx-input ns-select"
                value=""
                aria-label={t('ow.addRole')}
                onChange={(e) => add(e.target.value, 0)}
              >
                <option value="">{t('ow.addRole')}</option>
                {roleOptions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.id === guildId ? '@everyone' : r.name}
                  </option>
                ))}
              </Select>
              <Select
                className="nx-input ns-select"
                value=""
                aria-label={t('ow.addMember')}
                onChange={(e) => add(e.target.value, 1)}
              >
                <option value="">{t('ow.addMember')}</option>
                {memberOptions.map((m) => (
                  <option key={m.user.id} value={m.user.id}>
                    {displayName(m.user, m)}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
        </div>
        {selected ? (
          <div className="ns-chp-perms" data-target={selected.id}>
            <div className="ns-ow-h">
              <Icon name={selected.type === OverwriteType.ROLE ? 'shield' : 'user'} />
              <b dir="auto">{nameOf(selected)}</b>
              {canEdit && !locked(selected) ? (
                <button
                  type="button"
                  className="nx-ibtn sm flat"
                  aria-label={t('ow.remove')}
                  data-tip={t('ow.remove')}
                  onClick={() => {
                    const next = value.filter((x) => x.id !== selected.id);
                    onChange(next);
                    setSel(next[0]?.id ?? null);
                  }}
                >
                  <Icon name="trash" />
                </button>
              ) : null}
            </div>
            {locked(selected) ? <p className="ns-note warn">{t('ow.locked')}</p> : null}
            {gone(selected) ? (
              <p className="ns-note warn" data-gone="true">
                {t('ow.gone')}
              </p>
            ) : null}
            {groups.map((g) => (
              <div key={g.title} className="ns-perm-group">
                <h5>{t(g.title === 'ns.perm.g.general' ? 'ns.perm.g.generalChannel' : g.title)}</h5>
                {g.perms.map((def) => {
                  const label =
                    def.key === 'MANAGE_ROLES' ? t('ns.perm.MANAGE_ROLES.ch') : t(def.label);
                  const desc =
                    def.key === 'MANAGE_ROLES' ? t('ns.permd.MANAGE_ROLES.ch') : t(def.desc);
                  const off = !canEdit || locked(selected) || gone(selected) || !holds(def);
                  // Only the owner and Administrators allow Manage permissions in a channel.
                  return (
                    <div key={def.key} className="ns-ow-row" data-perm={def.key}>
                      <span className="ns-perm-txt">
                        <span>{label}</span>
                        <small className="ns-muted">{desc}</small>
                      </span>
                      <TriState
                        value={owValue(selected, def)}
                        label={label}
                        disabled={off}
                        why={
                          !canEdit
                            ? t('ns.admin.owNoPerm')
                            : locked(selected)
                              ? t('ow.locked')
                              : gone(selected)
                                ? t('ow.gone')
                                : t('roles.perm.notYours')
                        }
                        {...(def.key === 'MANAGE_ROLES' && !privileged
                          ? { allowOff: t('ow.adminOnly') }
                          : {})}
                        onChange={(v) =>
                          onChange(value.map((x) => (x.id === selected.id ? setOw(x, def, v) : x)))
                        }
                      />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
